/**
 * Talking to `/api/daily`.
 *
 * A plain module with no rune in it, the same reason `account-io.ts` and
 * `admin-io.ts` are: this is what `test/state/daily-io.test.ts` drives
 * against a stub `fetch`, and `$state` only exists once the Svelte compiler
 * has run. Reuses `account-io.ts`'s `request()` rather than a third copy of
 * "credentials, status mapping, never throw for a reason a player caused" —
 * `admin-io.ts` already set that precedent.
 */
import { request } from './account-io.js';
import type { AccountOutcome } from './account-io.js';

export const DAILY_PATH = '/api/daily';
export const DAILY_RESULT_PATH = '/api/daily/result';

export interface DailyBoardEntry {
  readonly username: string;
  readonly result: number;
}

/** Everything `GET /api/daily` answers. */
export interface DailyInfo {
  readonly date: string;
  /**
   * The instant the next daily begins, in epoch milliseconds. Optional: a
   * server from before `docs/decisions/0052` does not send it, and
   * `daily-countdown.ts` falls back to this device's own midnight.
   */
  readonly nextRolloverAtMs?: number;
  readonly seed: number;
  readonly goalLines: number;
  readonly hardness: 'normal';
  readonly played: boolean;
  readonly yourResult: number | null;
  readonly yourRank: number | null;
  readonly leaderboard: readonly DailyBoardEntry[];
}

/** A finished run, exactly what the server checks it against. See `apps/server/src/accounts/daily.ts`. */
export interface DailySubmission {
  readonly seed: number;
  readonly result: number;
  readonly boardHash: string;
  readonly pieceCount: number;
}

export interface DailySubmitResult {
  readonly result: number;
  readonly rank: number;
  readonly leaderboard: readonly DailyBoardEntry[];
}

/** Injected in tests; defaults to the page's own `fetch`. */
export type Fetcher = typeof globalThis.fetch;

/**
 * Today's board. Works signed out — a guest gets the leaderboard and the
 * seed, never a rank, per `docs/prompts/08-production.md`: "guests see the
 * leaderboard and can play unranked practice."
 */
export function fetchDaily(fetcher?: Fetcher): Promise<AccountOutcome<DailyInfo>> {
  return request(DAILY_PATH, {
    method: 'GET',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

/**
 * Post a finished daily run. Signed-in only — the server answers
 * `signed-out` for anybody else, which is `AccountOutcome`'s ordinary shape
 * for "there is no session," not a special case here.
 *
 * `refused` covers everything the server found wrong with the *result*
 * (stale seed, already played today, outside the sanity bounds) — the
 * server's own `message` is what a caller shows, because it already says
 * which of those happened in plain words.
 */
export function submitDailyResult(
  submission: DailySubmission,
  fetcher?: Fetcher,
): Promise<AccountOutcome<DailySubmitResult>> {
  return request(DAILY_RESULT_PATH, {
    method: 'POST',
    body: submission,
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}
